import { CircleAlert, CircleCheck, Plus, RefreshCw, Save, Store, Trash2, Undo2 } from 'lucide-react'
import { useEffect, useId, useState } from 'react'

import type { ErrorView, QueryView } from '@krp/client-runtime'
import { QueryState } from '@krp/sale-ui'
import { Button, CheckboxField, ModalFrame, ScreenInfo, SelectField, StatusMessage } from '@krp/ui-kit'

import '../../../shared/ui/aqsi.css'
import {
  PAYMENT_METHOD_LABELS,
  SUBJECT_LABELS,
  draftOf,
  hasProblems,
  isDirty,
  problemsOf,
  requestOf,
  withBarcode,
  MAX_BARCODES,
  type GoodProblems,
} from '../model/form'
import type { AqsiGoodCard, AqsiGoodEditRequest, GoodDraft } from '../model/types'

/** Справка окна: одна ⓘ сверху слева, обычными словами; у отдельных кнопок своих значков нет. */
const INFO_LINES = [
  'Это карточка товара в самом кабинете AQSI: здесь все поля, которые есть у товара в источнике.',
  'Карточка читается из кабинета при открытии. Внесите правку — внизу появятся «Сохранить» и «Отменить изменения».',
  'Сохранение пишет прямо в кабинет AQSI. Если карточку изменили в кабинете после того, как вы её открыли, запись не пройдёт: обновите карточку и повторите.',
  'Группа, тип и магазины здесь только для просмотра; свойства обмена с 1С не меняются.',
] as const

export interface GoodEditActions {
  reload: () => void
  save: (request: AqsiGoodEditRequest) => void
  dismissError: () => void
  close: () => void
  /** Сторож закрытия окна: пока есть несохранённая правка, крестик рамки и Esc сперва спрашивают. */
  closeGuard: (guard: (() => boolean) | null) => void
}

export interface GoodEditScreenProps {
  card: AqsiGoodCard | null
  query: QueryView
  saving: boolean
  saveError: ErrorView | null
  /** Итог последней записи словами: «Записано в кабинет…» или «Уже так в кабинете». */
  notice: string | null
  actions: GoodEditActions
}

type Confirm = 'discard' | 'reload' | 'close' | null

const timeOf = (iso: string | null): string => {
  if (iso === null) return ''
  const date = new Date(iso)
  return Number.isNaN(date.getTime()) ? iso : date.toLocaleString('ru-RU')
}

function options(labels: Readonly<Record<number, string>>, current: string) {
  const list = Object.entries(labels).map(([code, label]) => ({ value: code, label: `${code} — ${label}` }))
  return current === '' || list.some((item) => item.value === current)
    ? list
    : [{ value: current, label: `${current} — код вне списка` }, ...list]
}

function Field({
  label,
  value,
  onChange,
  problem,
  inputMode,
  note,
}: {
  label: string
  value: string
  onChange: (value: string) => void
  problem?: string | undefined
  inputMode?: 'numeric' | 'decimal' | 'text'
  note?: string
}) {
  const id = useId()
  return (
    <div className="krp-field aqsi-good__field">
      <label className="krp-field__label" htmlFor={id}>
        {label}
      </label>
      <input
        id={id}
        value={value}
        inputMode={inputMode}
        aria-invalid={problem !== undefined || undefined}
        onChange={(event) => onChange(event.target.value)}
      />
      {note !== undefined && <p className="aqsi-caption">{note}</p>}
      {problem !== undefined && (
        <p className="aqsi-good__problem" role="alert">
          <CircleAlert size={14} aria-hidden="true" /> {problem}
        </p>
      )}
    </div>
  )
}

/**
 * Окно товара источника AQSI: карточка кабинета со всеми полями и правка прямо в кабинете (решение владельца 23.10.2026). Вёрстка от
 * телефона (360 px): один столбец, цели нажатия не меньше 44 px, действия — в липкой панели внизу. Сверху — крестик рамки окна, внизу
 * либо «Закрыть», либо при правке «Сохранить» и «Отменить изменения» (с подтверждением). Отказ кабинета показывается дословно, с кодом и
 * номером запроса, копируется и не пропадает, пока человек не повторит запись или не обновит карточку.
 */
export function GoodEditScreen({ card, query, saving, saveError, notice, actions }: GoodEditScreenProps) {
  const [edit, setEdit] = useState<{ key: string; draft: GoodDraft } | null>(null)
  const [confirm, setConfirm] = useState<Confirm>(null)
  const key = card === null ? '' : `${card.goodId}|${card.updatedAt ?? ''}`
  const draft: GoodDraft | null = card === null ? null : edit !== null && edit.key === key ? edit.draft : draftOf(card)
  const dirty = card !== null && draft !== null && isDirty(card, draft)
  const problems: GoodProblems = draft === null ? {} : problemsOf(draft)
  const request = card !== null && draft !== null ? requestOf(card, draft) : null
  const canSave = dirty && request !== null && !saving

  const change = (patch: Partial<GoodDraft>) => {
    if (draft !== null) setEdit({ key, draft: { ...draft, ...patch } })
  }

  // Сторож закрытия окна и предупреждение браузера при перезагрузке: правка живёт только в памяти страницы.
  const { closeGuard } = actions
  useEffect(() => {
    if (!dirty) {
      closeGuard(null)
      return undefined
    }
    closeGuard(() => {
      setConfirm('close')
      return false
    })
    const onBeforeUnload = (event: BeforeUnloadEvent) => event.preventDefault()
    window.addEventListener('beforeunload', onBeforeUnload)
    return () => {
      closeGuard(null)
      window.removeEventListener('beforeunload', onBeforeUnload)
    }
  }, [closeGuard, dirty])

  const save = () => {
    if (canSave && request !== null) actions.save(request)
  }
  const discard = () => setEdit(null)
  const closeNow = () => {
    closeGuard(null)
    setConfirm(null)
    actions.close()
  }
  const reload = () => {
    if (dirty) setConfirm('reload')
    else actions.reload()
  }

  return (
    <section className="aqsi-screen aqsi-good" aria-label={card?.name ?? 'Товар AQSI'}>
      <header className="aqsi-good__head">
        <ScreenInfo title="Карточка товара в кабинете AQSI" lines={INFO_LINES} />
        <h2>{card?.name ?? 'Товар AQSI'}</h2>
        <Button variant="ghost" icon={<RefreshCw size={16} aria-hidden="true" />} busy={query.refreshing} disabled={saving} onClick={reload}>
          Обновить из кабинета
        </Button>
      </header>

      {card === null ? (
        <QueryState query={query} what="Карточка" emptyText="Товар не найден" onRetry={actions.reload} />
      ) : (
        <>
          {query.error !== null && <QueryState query={query} what="Карточка" emptyText="Товар не найден" onRetry={actions.reload} />}
          <p className="aqsi-caption aqsi-good__source">
            <Store size={14} aria-hidden="true" /> Источник: кабинет AQSI · ревизия карточки {timeOf(card.updatedAt) || 'неизвестна'}
          </p>
          {dirty && (
            <p className="aqsi-good__banner" role="status">
              <CircleAlert size={16} aria-hidden="true" /> Есть несохранённые изменения.
            </p>
          )}
          {draft !== null && (
            <form
              className="aqsi-form aqsi-good__form"
              onSubmit={(event) => {
                event.preventDefault()
                save()
              }}
            >
              <section className="aqsi-good__section" aria-labelledby="aqsi-good-main">
                <h3 id="aqsi-good-main">Основное</h3>
                <Field label="Наименование" value={draft.name} onChange={(name) => change({ name })} problem={problems.name} />
                <Field label="Артикул (SKU)" value={draft.sku} onChange={(sku) => change({ sku })} problem={problems.sku} />
                <Field
                  label="Цена, ₽"
                  value={draft.price}
                  inputMode="decimal"
                  onChange={(price) => change({ price })}
                  problem={problems.price}
                  note="Цена карточки; в кабинете отсутствующая цена хранится нулём."
                />
              </section>

              <section className="aqsi-good__section" aria-labelledby="aqsi-good-barcodes">
                <h3 id="aqsi-good-barcodes">Штрихкоды</h3>
                {draft.barcodes.length === 0 && <p className="aqsi-caption">Штрихкодов нет.</p>}
                <ul className="aqsi-good__list">
                  {draft.barcodes.map((code) => (
                    <li key={code}>
                      <span className="aqsi-good__list-main">{code}</span>
                      <Button
                        variant="ghost"
                        icon={<Trash2 size={16} aria-hidden="true" />}
                        aria-label={`Удалить штрихкод ${code}`}
                        onClick={() => change({ barcodes: draft.barcodes.filter((item) => item !== code) })}
                      >
                        Удалить
                      </Button>
                    </li>
                  ))}
                </ul>
                <Field
                  label="Новый штрихкод"
                  value={draft.newBarcode}
                  inputMode="numeric"
                  onChange={(newBarcode) => change({ newBarcode })}
                  problem={problems.barcodes}
                  note={`До ${MAX_BARCODES} штрихкодов, 4–22 цифры.`}
                />
                <Button
                  icon={<Plus size={16} aria-hidden="true" />}
                  disabled={draft.newBarcode.trim() === '' || draft.barcodes.length >= MAX_BARCODES}
                  onClick={() => setEdit({ key, draft: withBarcode(draft) })}
                >
                  Добавить штрихкод
                </Button>
              </section>

              <section className="aqsi-good__section" aria-labelledby="aqsi-good-tax">
                <h3 id="aqsi-good-tax">Налог и расчёт</h3>
                <Field
                  label="Код ставки НДС"
                  value={draft.tax}
                  inputMode="numeric"
                  onChange={(tax) => change({ tax })}
                  problem={problems.tax}
                  note="Кабинет отдаёт ставку кодом, без названия."
                />
                <SelectField label="Предмет расчёта" value={draft.subject} options={options(SUBJECT_LABELS, draft.subject)} onChange={(subject) => change({ subject })} />
                {problems.subject !== undefined && (
                  <p className="aqsi-good__problem" role="alert">
                    <CircleAlert size={14} aria-hidden="true" /> {problems.subject}
                  </p>
                )}
                <SelectField
                  label="Способ расчёта"
                  value={draft.paymentMethodType}
                  options={options(PAYMENT_METHOD_LABELS, draft.paymentMethodType)}
                  onChange={(paymentMethodType) => change({ paymentMethodType })}
                />
                {problems.paymentMethodType !== undefined && (
                  <p className="aqsi-good__problem" role="alert">
                    <CircleAlert size={14} aria-hidden="true" /> {problems.paymentMethodType}
                  </p>
                )}
              </section>

              <section className="aqsi-good__section" aria-labelledby="aqsi-good-unit">
                <h3 id="aqsi-good-unit">Единица измерения</h3>
                <Field label="Единица" value={draft.unit} onChange={(unit) => change({ unit })} problem={problems.unit} />
                <Field
                  label="Код меры (ОКЕИ)"
                  value={draft.unitCode}
                  inputMode="numeric"
                  onChange={(unitCode) => change({ unitCode })}
                  problem={problems.unitCode}
                  note="Например 796 — штука, 166 — килограмм. 10, 11 и 12 — весовые меры."
                />
                <CheckboxField label="Весовой товар" checked={draft.isWeight} onChange={(isWeight) => change({ isWeight })} />
              </section>

              {card.customProperties !== null && (
                <section className="aqsi-good__section" aria-labelledby="aqsi-good-props">
                  <h3 id="aqsi-good-props">Свойства карточки</h3>
                  {draft.properties.length === 0 && <p className="aqsi-caption">Своих свойств нет.</p>}
                  <ul className="aqsi-good__list">
                    {draft.properties.map((property, index) => (
                      <li key={index} className="aqsi-good__property">
                        <Field
                          label={`Ключ свойства ${index + 1}`}
                          value={property.key}
                          onChange={(value) =>
                            change({ properties: draft.properties.map((item, at) => (at === index ? { ...item, key: value } : item)) })
                          }
                        />
                        <Field
                          label={`Значение свойства ${index + 1}`}
                          value={property.value}
                          onChange={(value) =>
                            change({ properties: draft.properties.map((item, at) => (at === index ? { ...item, value } : item)) })
                          }
                        />
                        <Button
                          variant="ghost"
                          icon={<Trash2 size={16} aria-hidden="true" />}
                          aria-label={`Удалить свойство ${index + 1}`}
                          onClick={() => change({ properties: draft.properties.filter((_, at) => at !== index) })}
                        >
                          Удалить
                        </Button>
                      </li>
                    ))}
                  </ul>
                  {problems.properties !== undefined && (
                    <p className="aqsi-good__problem" role="alert">
                      <CircleAlert size={14} aria-hidden="true" /> {problems.properties}
                    </p>
                  )}
                  <Button icon={<Plus size={16} aria-hidden="true" />} onClick={() => change({ properties: [...draft.properties, { key: '', value: '' }] })}>
                    Добавить свойство
                  </Button>
                  {card.ownedPropertyKeys.length > 0 && (
                    <p className="aqsi-caption">Свойства обмена с 1С не меняются: {card.ownedPropertyKeys.join(', ')}.</p>
                  )}
                </section>
              )}
              {card.customProperties === null && (
                <p className="aqsi-caption">Свойства карточки прочитать не удалось: окно их не показывает и не меняет.</p>
              )}
            </form>
          )}

          <section className="aqsi-good__section" aria-labelledby="aqsi-good-readonly">
            <h3 id="aqsi-good-readonly">Только для просмотра</h3>
            <dl className="aqsi-facts">
              <dt>Ключ товара</dt>
              <dd>{card.goodId}</dd>
              <dt>Группа</dt>
              <dd>{card.groupId ?? 'нет'}</dd>
              <dt>Тип</dt>
              <dd>{card.type ?? 'simple'}</dd>
              <dt>Магазины</dt>
              <dd>
                {card.shops.length === 0
                  ? 'не привязан'
                  : card.shops.map((shop) => `${shop.name ?? shop.shopId}${shop.resourceShop ? ' (магазин этой кассы)' : ''}`).join(', ')}
              </dd>
            </dl>
          </section>
        </>
      )}

      <footer className="aqsi-good__bar" aria-label="Действия с карточкой">
        {saveError !== null && (
          <StatusMessage
            kind="error"
            title="Карточка в кабинете не записана"
            text={saveError.message}
            meta={`Код: ${saveError.code}${saveError.correlationId === null ? '' : ' · запрос ' + saveError.correlationId}`}
            action={
              <Button variant="ghost" onClick={actions.dismissError}>
                Скрыть сообщение
              </Button>
            }
          />
        )}
        {saveError === null && notice !== null && (
          <p className="aqsi-card__state" role="status" data-kind="done">
            <CircleCheck size={16} aria-hidden="true" /> {notice}
          </p>
        )}
        {dirty && hasProblems(problems) && <p className="aqsi-caption">Исправьте отмеченные поля — тогда можно сохранить.</p>}
        <div className="aqsi-good__buttons">
          {dirty ? (
            <>
              <Button variant="primary" icon={<Save size={16} aria-hidden="true" />} busy={saving} disabled={!canSave} onClick={save}>
                Сохранить
              </Button>
              <Button icon={<Undo2 size={16} aria-hidden="true" />} disabled={saving} onClick={() => setConfirm('discard')}>
                Отменить изменения
              </Button>
            </>
          ) : (
            <Button onClick={actions.close}>Закрыть</Button>
          )}
        </div>
      </footer>

      {confirm === 'discard' && (
        <ModalFrame title="Отменить изменения?" size="sm" covered={false} onClose={() => setConfirm(null)}>
          <div className="aqsi-good__confirm" role="alertdialog" aria-label="Отмена изменений">
            <p>Отменить несохранённые изменения?</p>
            <div className="aqsi-card__actions">
              <Button onClick={() => setConfirm(null)}>Нет</Button>
              <Button
                variant="primary"
                onClick={() => {
                  setConfirm(null)
                  discard()
                }}
              >
                Да
              </Button>
            </div>
          </div>
        </ModalFrame>
      )}

      {confirm === 'reload' && (
        <ModalFrame title="Обновить карточку?" size="sm" covered={false} onClose={() => setConfirm(null)}>
          <div className="aqsi-good__confirm" role="alertdialog" aria-label="Обновление с потерей правки">
            <p>Карточка будет прочитана из кабинета заново, несохранённые изменения пропадут. Продолжить?</p>
            <div className="aqsi-card__actions">
              <Button onClick={() => setConfirm(null)}>Нет</Button>
              <Button
                variant="primary"
                onClick={() => {
                  setConfirm(null)
                  discard()
                  actions.reload()
                }}
              >
                Да, обновить
              </Button>
            </div>
          </div>
        </ModalFrame>
      )}

      {confirm === 'close' && (
        <ModalFrame title="Закрыть карточку?" size="sm" covered={false} onClose={() => setConfirm(null)}>
          <div className="aqsi-good__confirm" role="alertdialog" aria-label="Несохранённые изменения">
            <p>Изменения не сохранены. Закрыть окно?</p>
            <div className="aqsi-card__actions">
              <Button onClick={() => setConfirm(null)}>Остаться</Button>
              <Button variant="primary" onClick={closeNow}>
                Закрыть без сохранения
              </Button>
            </div>
          </div>
        </ModalFrame>
      )}
    </section>
  )
}
